import { SPECULOOS_NAV_KIND, type SpecsEditorNav } from "./types";

export type SpecsNavHashRoute =
  | { kind: "empty" }
  | { kind: "settings" }
  | { kind: "history" }
  | { kind: "families"; specName?: string }
  | { kind: "fabrics"; specName?: string }
  | { kind: "decorations"; specName?: string }
  | { kind: "import" }
  | { kind: "familiesHistory"; specName?: string }
  | { kind: "fabricsHistory"; specName?: string }
  | { kind: "decorationsHistory"; specName?: string }
  | {
      kind: "positions";
      positionId?: string;
      part?: string;
      element?: string;
      style?: string;
      layerId?: string;
    }
  | { kind: "parts"; part?: string }
  | { kind: "elements" }
  | { kind: "styles" }
  | {
      kind: "layerSpecs";
      part?: string;
      element?: string;
      style?: string;
      layerName?: string;
    }
  | { kind: "shipping" }
  | { kind: "metadata" }
  | { kind: "global" }
  | {
      kind: "element";
      part: string;
      element: string;
      style: string;
    }
  | {
      kind: "layer";
      part: string;
      element: string;
      style: string;
      layerId?: string;
    };

function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

function decodeSegment(value: string): string {
  return decodeURIComponent(value);
}

export function serializeSpecsNavHash(nav: SpecsEditorNav): string {
  switch (nav.kind) {
    case SPECULOOS_NAV_KIND.EMPTY:
      return "";
    case SPECULOOS_NAV_KIND.SETTINGS:
      return `#${SPECULOOS_NAV_KIND.SETTINGS}`;
    case SPECULOOS_NAV_KIND.HISTORY:
      return `#${SPECULOOS_NAV_KIND.HISTORY}`;
    case SPECULOOS_NAV_KIND.FAMILIES:
      return nav.specName
        ? `#${SPECULOOS_NAV_KIND.FAMILIES}/${encodeSegment(nav.specName)}`
        : `#${SPECULOOS_NAV_KIND.FAMILIES}`;
    case SPECULOOS_NAV_KIND.FABRICS:
      return nav.specName
        ? `#${SPECULOOS_NAV_KIND.FABRICS}/${encodeSegment(nav.specName)}`
        : `#${SPECULOOS_NAV_KIND.FABRICS}`;
    case SPECULOOS_NAV_KIND.DECORATIONS:
      return nav.specName
        ? `#${SPECULOOS_NAV_KIND.DECORATIONS}/${encodeSegment(nav.specName)}`
        : `#${SPECULOOS_NAV_KIND.DECORATIONS}`;
    case SPECULOOS_NAV_KIND.IMPORT:
      return `#${SPECULOOS_NAV_KIND.IMPORT}`;
    case SPECULOOS_NAV_KIND.FAMILIES_HISTORY:
      return nav.specName
        ? `#${SPECULOOS_NAV_KIND.FAMILIES_HISTORY}/${encodeSegment(nav.specName)}`
        : `#${SPECULOOS_NAV_KIND.FAMILIES_HISTORY}`;
    case SPECULOOS_NAV_KIND.FABRICS_HISTORY:
      return nav.specName
        ? `#${SPECULOOS_NAV_KIND.FABRICS_HISTORY}/${encodeSegment(nav.specName)}`
        : `#${SPECULOOS_NAV_KIND.FABRICS_HISTORY}`;
    case SPECULOOS_NAV_KIND.DECORATIONS_HISTORY:
      return nav.specName
        ? `#${SPECULOOS_NAV_KIND.DECORATIONS_HISTORY}/${encodeSegment(nav.specName)}`
        : `#${SPECULOOS_NAV_KIND.DECORATIONS_HISTORY}`;
    case SPECULOOS_NAV_KIND.POSITIONS:
      return (() => {
        const base = nav.positionId
          ? `#${SPECULOOS_NAV_KIND.POSITIONS}/${encodeSegment(nav.positionId)}`
          : `#${SPECULOOS_NAV_KIND.POSITIONS}`;
        if (!nav.part || !nav.element || !nav.style) {
          return base;
        }
        return `${base}/scope/${encodeSegment(nav.part)}/${encodeSegment(nav.element)}/${encodeSegment(nav.style)}`;
      })();
    case SPECULOOS_NAV_KIND.PARTS:
      return nav.part
        ? `#${SPECULOOS_NAV_KIND.PARTS}/${encodeSegment(nav.part)}`
        : `#${SPECULOOS_NAV_KIND.PARTS}`;
    case SPECULOOS_NAV_KIND.ELEMENTS:
      return `#${SPECULOOS_NAV_KIND.ELEMENTS}`;
    case SPECULOOS_NAV_KIND.STYLES:
      return `#${SPECULOOS_NAV_KIND.STYLES}`;
    case SPECULOOS_NAV_KIND.LAYER_SPECS:
      if (!nav.part || !nav.element || !nav.style) {
        return `#${SPECULOOS_NAV_KIND.LAYER_SPECS}`;
      }
      return `#${SPECULOOS_NAV_KIND.LAYER_SPECS}/${encodeSegment(nav.part)}/${encodeSegment(nav.element)}/${encodeSegment(nav.style)}${
        nav.layerName ? `/${encodeSegment(nav.layerName)}` : ""
      }`;
    case SPECULOOS_NAV_KIND.SHIPPING:
      return `#${SPECULOOS_NAV_KIND.SHIPPING}`;
    case SPECULOOS_NAV_KIND.METADATA:
      return `#${SPECULOOS_NAV_KIND.METADATA}`;
    case SPECULOOS_NAV_KIND.GLOBAL:
      return `#${SPECULOOS_NAV_KIND.GLOBAL}`;
    case SPECULOOS_NAV_KIND.ELEMENT:
      return `#${SPECULOOS_NAV_KIND.ELEMENT}/${encodeSegment(nav.part)}/${encodeSegment(nav.element)}/${encodeSegment(nav.style)}`;
    case SPECULOOS_NAV_KIND.LAYER:
      return `#${SPECULOOS_NAV_KIND.LAYER}/${encodeSegment(nav.part)}/${encodeSegment(nav.element)}/${encodeSegment(nav.style)}`;
    default:
      return "";
  }
}

export function parseSpecsNavHash(
  hash: string | null | undefined
): SpecsNavHashRoute | null {
  if (!hash) return null;
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const parts = trimmed.split("/").filter(Boolean).map(decodeSegment);
  const [kind, ...rest] = parts;

  switch (kind) {
    case SPECULOOS_NAV_KIND.EMPTY:
      return { kind: SPECULOOS_NAV_KIND.EMPTY };
    case SPECULOOS_NAV_KIND.SETTINGS:
      return { kind: SPECULOOS_NAV_KIND.SETTINGS };
    case SPECULOOS_NAV_KIND.HISTORY:
      return { kind: SPECULOOS_NAV_KIND.HISTORY };
    case SPECULOOS_NAV_KIND.FAMILIES:
      return {
        kind: SPECULOOS_NAV_KIND.FAMILIES,
        specName: rest[0],
      };
    case SPECULOOS_NAV_KIND.FABRICS:
      return {
        kind: SPECULOOS_NAV_KIND.FABRICS,
        specName: rest[0],
      };
    case SPECULOOS_NAV_KIND.DECORATIONS:
      return {
        kind: SPECULOOS_NAV_KIND.DECORATIONS,
        specName: rest[0],
      };
    case SPECULOOS_NAV_KIND.IMPORT:
      return { kind: SPECULOOS_NAV_KIND.IMPORT };
    case SPECULOOS_NAV_KIND.FAMILIES_HISTORY:
      return {
        kind: SPECULOOS_NAV_KIND.FAMILIES_HISTORY,
        specName: rest[0],
      };
    case SPECULOOS_NAV_KIND.FABRICS_HISTORY:
      return {
        kind: SPECULOOS_NAV_KIND.FABRICS_HISTORY,
        specName: rest[0],
      };
    case SPECULOOS_NAV_KIND.DECORATIONS_HISTORY:
      return {
        kind: SPECULOOS_NAV_KIND.DECORATIONS_HISTORY,
        specName: rest[0],
      };
    case SPECULOOS_NAV_KIND.POSITIONS:
      if (rest[0] === "scope" && rest.length >= 4) {
        return {
          kind: SPECULOOS_NAV_KIND.POSITIONS,
          part: rest[1],
          element: rest[2],
          style: rest[3],
          layerId: rest[4],
        };
      }
      if (rest[1] === "scope" && rest.length >= 5) {
        return {
          kind: SPECULOOS_NAV_KIND.POSITIONS,
          positionId: rest[0],
          part: rest[2],
          element: rest[3],
          style: rest[4],
          layerId: rest[5],
        };
      }
      return rest[0]
        ? { kind: SPECULOOS_NAV_KIND.POSITIONS, positionId: rest[0] }
        : { kind: SPECULOOS_NAV_KIND.POSITIONS };
    case SPECULOOS_NAV_KIND.PARTS:
      return rest[0]
        ? { kind: SPECULOOS_NAV_KIND.PARTS, part: rest[0] }
        : { kind: SPECULOOS_NAV_KIND.PARTS };
    case SPECULOOS_NAV_KIND.ELEMENTS:
      return { kind: SPECULOOS_NAV_KIND.ELEMENTS };
    case SPECULOOS_NAV_KIND.STYLES:
      return { kind: SPECULOOS_NAV_KIND.STYLES };
    case SPECULOOS_NAV_KIND.LAYER_SPECS:
      if (rest.length === 0) {
        return { kind: SPECULOOS_NAV_KIND.LAYER_SPECS };
      }
      if (rest.length < 3) return null;
      return {
        kind: SPECULOOS_NAV_KIND.LAYER_SPECS,
        part: rest[0],
        element: rest[1],
        style: rest[2],
        layerName: rest[3],
      };
    case SPECULOOS_NAV_KIND.SHIPPING:
      return { kind: SPECULOOS_NAV_KIND.SHIPPING };
    case SPECULOOS_NAV_KIND.METADATA:
      return { kind: SPECULOOS_NAV_KIND.METADATA };
    case SPECULOOS_NAV_KIND.GLOBAL:
      return { kind: SPECULOOS_NAV_KIND.GLOBAL };
    case SPECULOOS_NAV_KIND.ELEMENT:
      if (rest.length < 3) return null;
      return {
        kind: SPECULOOS_NAV_KIND.ELEMENT,
        part: rest[0],
        element: rest[1],
        style: rest[2],
      };
    case SPECULOOS_NAV_KIND.LAYER:
      if (rest.length < 3) return null;
      return {
        kind: SPECULOOS_NAV_KIND.LAYER,
        part: rest[0],
        element: rest[1],
        style: rest[2],
        layerId: rest[3],
      };
    default:
      return null;
  }
}
